import type { ChangeEventHandler } from 'react';
import { useEffect, useState } from 'react';
import { useHeaderData } from '@screentone/addon-auth-wrapper';
import type { ComboboxItem } from '@screentone/core';
import { Box, FormHelperText, FormLabel, Group, Input, MultiSelect, Typography } from '@screentone/core';
import { useQueryClient } from '@tanstack/react-query';

import { ResponsiveLoader } from 'components';
import { Accordion } from 'components/accordion/Accordion';
import { useAlert } from 'contexts/alert/useAlert';
import { PublicationSettingUpdateInput, usePublicationSettingUpdateMutation } from 'data/generated/graphql';
import UnifiedQueryConfigTable from 'features/base-query-config/UnifiedQueryConfigTable';
import { useBaseQueryField } from 'features/publication-settings/useBaseQueryField';
import PublicationSettingsActionBar from 'features/publication-settings-common/PublicationSettingsActionBar';
import usePublicationSettingsLock from 'features/publication-settings-common/usePublicationSettingsLock';
import { useConvertedProperty } from 'hooks';
import { usePublicationSettings } from 'hooks/publication-settings';

const toMultiselectItem = (value: string): ComboboxItem => ({ id: value, label: value, value });

const PublicationSettingsContentSources = () => {
  const [currentPublicationSetting, setCurrentPublicationSetting] = useState<PublicationSettingUpdateInput | null>(
    null
  );
  const [hasChanged, setHasChanged] = useState(false);

  const currentProperty = useConvertedProperty();
  const {
    state: { header }
  } = useHeaderData();
  const properties = header?.properties;
  const { alertError, alertSuccess } = useAlert();
  const { publishingFragment, lockIsReady, lockLoading, isPageLockingEnabled, handleLock, handleRelease } =
    usePublicationSettingsLock();

  const publicationSettingsQueryVariables = { publicationKey: currentProperty ?? '' };
  const { data, isLoading: isLoad, fetchStatus } = usePublicationSettings(publicationSettingsQueryVariables);
  const isLoading = isLoad && fetchStatus !== 'idle';

  const selectedEditableExternalPublications = currentPublicationSetting?.editableExternalPublications ?? [];

  const handleEditableExternalPublicationsChange = (
    items: ComboboxItem[] | ((prev: ComboboxItem[]) => ComboboxItem[])
  ) => {
    if (currentPublicationSetting === null) return;
    const resolved = typeof items === 'function' ? items(selectedEditableExternalPublications.map(toMultiselectItem)) : items;
    setCurrentPublicationSetting({
      ...currentPublicationSetting,
      editableExternalPublications: resolved.map((item) => item.value as string)
    });
    setHasChanged(true);
  };

  const { mutateAsync, mutate, isLoading: isUpdating } = usePublicationSettingUpdateMutation();

  const queryClient = useQueryClient();

  const baseAllessehQuery = useBaseQueryField(data?.publicationSetting.baseAllessehQuery);
  const baseProductCardAllessehQuery = useBaseQueryField(data?.publicationSetting.baseProductCardAllessehQuery);
  const baseAudioAllessehQuery = useBaseQueryField(data?.publicationSetting.baseAudioAllessehQuery);
  const baseVideoAllessehQuery = useBaseQueryField(data?.publicationSetting.baseVideoAllessehQuery);
  const baseRecipeAllessehQuery = useBaseQueryField(data?.publicationSetting.baseRecipeAllessehQuery);
  const baseWebStoryAllessehQuery = useBaseQueryField(data?.publicationSetting.baseWebStoryAllessehQuery);
  const baseOpinionAllessehQuery = useBaseQueryField(data?.publicationSetting.baseOpinionAllessehQuery);
  const baseWsjProAllessehQuery = useBaseQueryField(data?.publicationSetting.baseWsjProAllessehQuery);
  const baseLiveCoverageAllessehQuery = useBaseQueryField(data?.publicationSetting.baseLiveCoverageAllessehQuery);
  const baseTappableAllessehQuery = useBaseQueryField(data?.publicationSetting.baseTappableAllessehQuery);
  const basePodcastAllessehQuery = useBaseQueryField(data?.publicationSetting.basePodcastAllessehQuery);
  const baseMarketwatchAllessehQuery = useBaseQueryField(data?.publicationSetting.baseMarketwatchAllessehQuery);
  const baseMarketwatchVideoAllessehQuery = useBaseQueryField(
    data?.publicationSetting.baseMarketwatchVideoAllessehQuery
  );
  const baseMarketwatchLiveCoverageCardsAllessehQuery = useBaseQueryField(
    data?.publicationSetting.baseMarketwatchLiveCoverageCardsAllessehQuery
  );
  const baseMarketwatchLiveCoverageEventsAllessehQuery = useBaseQueryField(
    data?.publicationSetting.baseMarketwatchLiveCoverageEventsAllessehQuery
  );
  const baseWsjAllessehQuery = useBaseQueryField(data?.publicationSetting.baseWsjAllessehQuery);
  const baseWsjVideoAllessehQuery = useBaseQueryField(data?.publicationSetting.baseWsjVideoAllessehQuery);
  const baseWsjLiveCoverageCardsAllessehQuery = useBaseQueryField(
    data?.publicationSetting.baseWsjLiveCoverageCardsAllessehQuery
  );
  const baseWsjLiveCoverageEventsAllessehQuery = useBaseQueryField(
    data?.publicationSetting.baseWsjLiveCoverageEventsAllessehQuery
  );
  const baseMansionGlobalAllessehQuery = useBaseQueryField(data?.publicationSetting.baseMansionGlobalAllessehQuery);
  const baseBarronsInvestorCircleAllessehQuery = useBaseQueryField(
    data?.publicationSetting.baseBarronsInvestorCircleAllessehQuery
  );
  const baseDowJonesRiskJournalAllessehQuery = useBaseQueryField(
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    data?.publicationSetting.baseDowJonesRiskJournalAllessehQuery
  );
  const baseDowJonesRiskJournalDJEventsAllessehQuery = useBaseQueryField(
    data?.publicationSetting.baseDowJonesRiskJournalDJEventsAllessehQuery
  );
  const baseDowJonesRiskJournalCCOEventsAllessehQuery = useBaseQueryField(
    data?.publicationSetting.baseDowJonesRiskJournalCCOEventsAllessehQuery
  );
  const baseDowJonesRiskJournalContributorAllessehQuery = useBaseQueryField(
    data?.publicationSetting.baseDowJonesRiskJournalContributorAllessehQuery
  );
  const baseDragonflyAllessehQuery = useBaseQueryField(data?.publicationSetting.baseDragonflyAllessehQuery);
  const basePENEWSAllessehQuery = useBaseQueryField(data?.publicationSetting.basePENEWSAllessehQuery);
  const baseAfpAllessehQuery = useBaseQueryField(data?.publicationSetting.baseAfpAllessehQuery);
  const baseBarronsAllessehQuery = useBaseQueryField(data?.publicationSetting.baseBarronsAllessehQuery);
  const baseBarronsVideoAllessehQuery = useBaseQueryField(data?.publicationSetting.baseBarronsVideoAllessehQuery);
  const baseBarronsLiveCoverageEventsAllessehQuery = useBaseQueryField(
    data?.publicationSetting.baseBarronsLiveCoverageEventsAllessehQuery
  );
  const baseBarronsLiveCoverageCardsAllessehQuery = useBaseQueryField(
    data?.publicationSetting.baseBarronsLiveCoverageCardsAllessehQuery
  );
  const baseExportComplianceManagerAllessehQuery = useBaseQueryField(
    data?.publicationSetting.baseExportComplianceManagerAllessehQuery
  );
  const baseFNOnlineAllessehQuery = useBaseQueryField(data?.publicationSetting.baseFNOnlineAllessehQuery);
  const baseFreeExpressionAllessehQuery = useBaseQueryField(data?.publicationSetting.baseFreeExpressionAllessehQuery);
  const baseOxfordAnalyticaAllessehQuery = useBaseQueryField(data?.publicationSetting.baseOxfordAnalyticaAllessehQuery);
  const baseOxfordAnalyticaAnnouncementsAllessehQuery = useBaseQueryField(
    data?.publicationSetting.baseOxfordAnalyticaAnnouncementsAllessehQuery
  );
  const basePentaAllessehQuery = useBaseQueryField(data?.publicationSetting.basePentaAllessehQuery);
  const baseWorldECRAllessehQuery = useBaseQueryField(data?.publicationSetting.baseWorldECRAllessehQuery);
  const baseWSJOpinionVideoAllessehQuery = useBaseQueryField(data?.publicationSetting.baseWSJOpinionVideoAllessehQuery);
  const baseBarronsBlogAllessehQuery = useBaseQueryField(data?.publicationSetting.baseBarronsBlogAllessehQuery);
  const baseIbdAllessehQuery = useBaseQueryField(data?.publicationSetting.baseIbdAllessehQuery);
  const baseWsjProPEAllessehQuery = useBaseQueryField(data?.publicationSetting.baseWsjProPEAllessehQuery);
  const baseOpisAllessehQuery = useBaseQueryField(data?.publicationSetting.baseOpisAllessehQuery);
  const baseEnergyNewsEventsAllessehQuery = useBaseQueryField(
    data?.publicationSetting.baseEnergyNewsEventsAllessehQuery
  );
  const basePrivateMarketEventsAllessehQuery = useBaseQueryField(
    data?.publicationSetting.basePrivateMarketEventsAllessehQuery
  );

  const jsonFields = [
    baseAllessehQuery,
    baseProductCardAllessehQuery,
    baseAudioAllessehQuery,
    baseVideoAllessehQuery,
    baseRecipeAllessehQuery,
    baseWebStoryAllessehQuery,
    baseOpinionAllessehQuery,
    baseWsjProAllessehQuery,
    baseWSJOpinionVideoAllessehQuery,
    baseLiveCoverageAllessehQuery,
    baseTappableAllessehQuery,
    basePodcastAllessehQuery,
    baseMarketwatchAllessehQuery,
    baseMarketwatchVideoAllessehQuery,
    baseMarketwatchLiveCoverageCardsAllessehQuery,
    baseMarketwatchLiveCoverageEventsAllessehQuery,
    baseWsjAllessehQuery,
    baseWsjVideoAllessehQuery,
    baseWsjLiveCoverageCardsAllessehQuery,
    baseWsjLiveCoverageEventsAllessehQuery,
    baseMansionGlobalAllessehQuery,
    baseBarronsAllessehQuery,
    baseBarronsVideoAllessehQuery,
    baseBarronsInvestorCircleAllessehQuery,
    baseBarronsLiveCoverageEventsAllessehQuery,
    baseBarronsLiveCoverageCardsAllessehQuery,
    baseDowJonesRiskJournalAllessehQuery,
    baseDowJonesRiskJournalDJEventsAllessehQuery,
    baseDowJonesRiskJournalCCOEventsAllessehQuery,
    baseDowJonesRiskJournalContributorAllessehQuery,
    baseDragonflyAllessehQuery,
    basePENEWSAllessehQuery,
    baseAfpAllessehQuery,
    baseExportComplianceManagerAllessehQuery,
    baseFNOnlineAllessehQuery,
    baseFreeExpressionAllessehQuery,
    baseOxfordAnalyticaAllessehQuery,
    baseOxfordAnalyticaAnnouncementsAllessehQuery,
    basePentaAllessehQuery,
    baseWorldECRAllessehQuery,
    baseBarronsBlogAllessehQuery,
    baseIbdAllessehQuery,
    baseWsjProPEAllessehQuery,
    baseOpisAllessehQuery,
    baseEnergyNewsEventsAllessehQuery,
    basePrivateMarketEventsAllessehQuery
  ];

  useEffect(() => {
    if (data) {
      setCurrentPublicationSetting(data.publicationSetting);
    }
  }, [data]);

  const handleInputNumberChange =
    (fieldName: keyof PublicationSettingUpdateInput): ChangeEventHandler<HTMLInputElement> =>
    (e: React.FormEvent<HTMLInputElement>) => {
      if (currentPublicationSetting === null) {
        return;
      }
      const { value } = e.target as HTMLInputElement;
      if (typeof value !== 'string' || value.trim() === '' || Number.isNaN(Number(value))) {
        setCurrentPublicationSetting({ ...currentPublicationSetting, [fieldName]: null });
      } else {
        setCurrentPublicationSetting({ ...currentPublicationSetting, [fieldName]: Number(value) });
      }
      setHasChanged(true);
    };

  const buildPublicationSettingInput = (): PublicationSettingUpdateInput => ({
    ...currentPublicationSetting!,
    baseAllessehQuery: baseAllessehQuery.parseValue()?.tryStringify() ?? '',
    baseProductCardAllessehQuery: baseProductCardAllessehQuery.parseValue()?.tryStringify(),
    baseAudioAllessehQuery: baseAudioAllessehQuery.parseValue()?.tryStringify(),
    baseVideoAllessehQuery: baseVideoAllessehQuery.parseValue()?.tryStringify(),
    baseRecipeAllessehQuery: baseRecipeAllessehQuery.parseValue()?.tryStringify(),
    baseWebStoryAllessehQuery: baseWebStoryAllessehQuery.parseValue()?.tryStringify(),
    baseOpinionAllessehQuery: baseOpinionAllessehQuery.parseValue()?.tryStringify(),
    baseWsjProAllessehQuery: baseWsjProAllessehQuery.parseValue()?.tryStringify(),
    baseWSJOpinionVideoAllessehQuery: baseWSJOpinionVideoAllessehQuery.parseValue()?.tryStringify(),
    baseLiveCoverageAllessehQuery: baseLiveCoverageAllessehQuery.parseValue()?.tryStringify(),
    baseTappableAllessehQuery: baseTappableAllessehQuery.parseValue()?.tryStringify(),
    basePodcastAllessehQuery: basePodcastAllessehQuery.parseValue()?.tryStringify(),
    baseMarketwatchAllessehQuery: baseMarketwatchAllessehQuery.parseValue()?.tryStringify(),
    baseMarketwatchVideoAllessehQuery: baseMarketwatchVideoAllessehQuery.parseValue()?.tryStringify(),
    baseMarketwatchLiveCoverageCardsAllessehQuery: baseMarketwatchLiveCoverageCardsAllessehQuery
      .parseValue()
      ?.tryStringify(),
    baseMarketwatchLiveCoverageEventsAllessehQuery: baseMarketwatchLiveCoverageEventsAllessehQuery
      .parseValue()
      ?.tryStringify(),
    baseWsjVideoAllessehQuery: baseWsjVideoAllessehQuery.parseValue()?.tryStringify(),
    baseWsjLiveCoverageCardsAllessehQuery: baseWsjLiveCoverageCardsAllessehQuery.parseValue()?.tryStringify(),
    baseWsjLiveCoverageEventsAllessehQuery: baseWsjLiveCoverageEventsAllessehQuery.parseValue()?.tryStringify(),
    baseMansionGlobalAllessehQuery: baseMansionGlobalAllessehQuery.parseValue()?.tryStringify(),
    baseBarronsAllessehQuery: baseBarronsAllessehQuery.parseValue()?.tryStringify(),
    baseBarronsVideoAllessehQuery: baseBarronsVideoAllessehQuery.parseValue()?.tryStringify(),
    baseBarronsInvestorCircleAllessehQuery: baseBarronsInvestorCircleAllessehQuery.parseValue()?.tryStringify(),
    baseBarronsLiveCoverageEventsAllessehQuery: baseBarronsLiveCoverageEventsAllessehQuery.parseValue()?.tryStringify(),
    baseBarronsLiveCoverageCardsAllessehQuery: baseBarronsLiveCoverageCardsAllessehQuery.parseValue()?.tryStringify(),
    baseDowJonesRiskJournalAllessehQuery: baseDowJonesRiskJournalAllessehQuery.parseValue()?.tryStringify(),
    baseDowJonesRiskJournalDJEventsAllessehQuery: baseDowJonesRiskJournalDJEventsAllessehQuery
      .parseValue()
      ?.tryStringify(),
    baseDowJonesRiskJournalCCOEventsAllessehQuery: baseDowJonesRiskJournalCCOEventsAllessehQuery
      .parseValue()
      ?.tryStringify(),
    baseDowJonesRiskJournalContributorAllessehQuery: baseDowJonesRiskJournalContributorAllessehQuery
      .parseValue()
      ?.tryStringify(),
    baseDragonflyAllessehQuery: baseDragonflyAllessehQuery.parseValue()?.tryStringify(),
    basePENEWSAllessehQuery: basePENEWSAllessehQuery.parseValue()?.tryStringify(),
    baseExportComplianceManagerAllessehQuery: baseExportComplianceManagerAllessehQuery.parseValue()?.tryStringify(),
    baseFNOnlineAllessehQuery: baseFNOnlineAllessehQuery.parseValue()?.tryStringify(),
    baseFreeExpressionAllessehQuery: baseFreeExpressionAllessehQuery.parseValue()?.tryStringify(),
    baseOxfordAnalyticaAllessehQuery: baseOxfordAnalyticaAllessehQuery.parseValue()?.tryStringify(),
    baseOxfordAnalyticaAnnouncementsAllessehQuery: baseOxfordAnalyticaAnnouncementsAllessehQuery
      .parseValue()
      ?.tryStringify(),
    basePentaAllessehQuery: basePentaAllessehQuery.parseValue()?.tryStringify(),
    baseWorldECRAllessehQuery: baseWorldECRAllessehQuery.parseValue()?.tryStringify(),
    baseBarronsBlogAllessehQuery: baseBarronsBlogAllessehQuery.parseValue()?.tryStringify(),
    baseWsjAllessehQuery: baseWsjAllessehQuery.parseValue()?.tryStringify(),
    baseAfpAllessehQuery: baseAfpAllessehQuery.parseValue()?.tryStringify(),
    baseIbdAllessehQuery: baseIbdAllessehQuery.parseValue()?.tryStringify(),
    baseWsjProPEAllessehQuery: baseWsjProPEAllessehQuery.parseValue()?.tryStringify(),
    baseOpisAllessehQuery: baseOpisAllessehQuery.parseValue()?.tryStringify(),
    baseEnergyNewsEventsAllessehQuery: baseEnergyNewsEventsAllessehQuery.parseValue()?.tryStringify(),
    basePrivateMarketEventsAllessehQuery: basePrivateMarketEventsAllessehQuery.parseValue()?.tryStringify(),
    liveCoverageSourceUrls:
      ((currentPublicationSetting!.liveCoverageSourceUrls as string[] | null | undefined) ?? []).length > 0
        ? (currentPublicationSetting!.liveCoverageSourceUrls as string[])
        : null
  });

  const handleSave = () => {
    if (currentPublicationSetting === null || !currentProperty) {
      return;
    }

    let newPublicationSetting: PublicationSettingUpdateInput;
    try {
      newPublicationSetting = buildPublicationSettingInput();
    } catch (err) {
      alertError(`Cannot save — fix the highlighted JSON field(s) first. (${(err as Error).message})`);
      return;
    }

    mutate(
      {
        publicationSettingUpdateInput: newPublicationSetting
      },
      {
        onSuccess: async () => {
          setCurrentPublicationSetting(newPublicationSetting);
          setHasChanged(false);
          jsonFields.forEach((field) => field.clearError());
          alertSuccess('Publication settings saved successfully');

          await queryClient.invalidateQueries({
            queryKey: ['PublicationSettings', publicationSettingsQueryVariables],
            exact: false
          });
        },
        onError: (error) => {
          alertError(`Failed to save publication settings. ${(error as Error).message}`);
        }
      }
    );
  };

  const baseQueryFields = [
    {
      key: 'base',
      label: 'Base Allesseh Query',
      placeholder: 'Enter the JSON Allesseh query that should be used for article queries in this publication',
      field: baseAllessehQuery
    },
    {
      key: 'baseProductCard',
      label: 'Base Product Card Allesseh Query',
      placeholder: 'Enter the JSON Allesseh query that should be used for product card queries in this publication',
      field: baseProductCardAllessehQuery
    },
    {
      key: 'baseAudio',
      label: 'Base Audio Allesseh Query',
      placeholder: 'Enter the JSON Allesseh query for audio queries',
      field: baseAudioAllessehQuery
    },
    {
      key: 'baseVideo',
      label: 'Base Video Allesseh Query',
      placeholder: 'Enter the JSON Allesseh query for video queries',
      field: baseVideoAllessehQuery
    },
    {
      key: 'baseRecipe',
      label: 'Base Recipe Allesseh Query',
      placeholder: 'Enter the JSON Allesseh query for recipe queries',
      field: baseRecipeAllessehQuery
    },
    {
      key: 'baseWebStory',
      label: 'Base Web Story Allesseh Query',
      placeholder: 'Enter the JSON Allesseh query for web story queries',
      field: baseWebStoryAllessehQuery
    },
    {
      key: 'baseOpinion',
      label: 'Base Opinion Allesseh Query',
      placeholder: 'Enter the JSON Allesseh query for opinion queries',
      field: baseOpinionAllessehQuery
    },
    {
      key: 'baseWsj',
      label: 'Base WSJ Allesseh Query',
      placeholder: 'Enter the JSON Allesseh query for WSJ queries',
      field: baseWsjAllessehQuery
    },
    {
      key: 'baseWsjVideo',
      label: 'Base WSJ Video Allesseh Query',
      placeholder: 'Enter the JSON Allesseh query for WSJ Video queries',
      field: baseWsjVideoAllessehQuery
    },
    {
      key: 'baseWsjLiveCoverageCards',
      label: 'Base WSJ Live Coverage Cards Allesseh Query',
      placeholder: 'Enter the JSON Allesseh query for WSJ Live Coverage Cards queries',
      field: baseWsjLiveCoverageCardsAllessehQuery
    },
    {
      key: 'baseWsjLiveCoverageEvents',
      label: 'Base WSJ Live Coverage Events Allesseh Query',
      placeholder: 'Enter the JSON Allesseh query for WSJ Live Coverage Events queries',
      field: baseWsjLiveCoverageEventsAllessehQuery
    },
    {
      key: 'baseWsjPro',
      label: 'Base WSJ Pro Allesseh Query',
      placeholder: 'Enter the JSON Allesseh query for WSJ Pro queries',
      field: baseWsjProAllessehQuery
    },
    {
      key: 'baseWSJOpinionVideo',
      label: 'Base WSJ Opinion Video Allesseh Query',
      placeholder: 'Enter the JSON Allesseh query for WSJ Opinion Video queries',
      field: baseWSJOpinionVideoAllessehQuery
    },
    {
      key: 'baseLiveCoverage',
      label: 'Base Live Coverage Allesseh Query',
      placeholder: 'Enter the JSON Allesseh query for live coverage queries',
      field: baseLiveCoverageAllessehQuery
    },
    {
      key: 'baseTappable',
      label: 'Base Tappable Allesseh Query',
      placeholder: 'Enter the JSON Allesseh query for tappable queries',
      field: baseTappableAllessehQuery
    },
    {
      key: 'basePodcast',
      label: 'Base Podcast Allesseh Query',
      placeholder: 'Enter the JSON Allesseh query for podcast queries',
      field: basePodcastAllessehQuery
    },
    {
      key: 'baseMarketwatch',
      label: 'Base Marketwatch Allesseh Query',
      placeholder: 'Enter the JSON Allesseh query for Marketwatch queries',
      field: baseMarketwatchAllessehQuery
    },
    {
      key: 'baseMarketwatchVideo',
      label: 'Base Marketwatch Video Allesseh Query',
      placeholder: 'Enter the JSON Allesseh query for Marketwatch Video queries',
      field: baseMarketwatchVideoAllessehQuery
    },
    {
      key: 'baseMarketwatchLiveCoverageCards',
      label: 'Base MarketWatch Live Coverage Cards Allesseh Query',
      placeholder: 'Enter the JSON Allesseh query for MarketWatch Live Coverage Cards queries',
      field: baseMarketwatchLiveCoverageCardsAllessehQuery
    },
    {
      key: 'baseMarketwatchLiveCoverageEvents',
      label: 'Base MarketWatch Live Coverage Events Allesseh Query',
      placeholder: 'Enter the JSON Allesseh query for MarketWatch Live Coverage Events queries',
      field: baseMarketwatchLiveCoverageEventsAllessehQuery
    },
    {
      key: 'baseMansionGlobal',
      label: 'Base Mansion Global Allesseh Query',
      placeholder: 'Enter the JSON Allesseh query for Mansion Global queries',
      field: baseMansionGlobalAllessehQuery
    },
    {
      key: 'baseBarrons',
      label: 'Base Barrons Allesseh Query',
      placeholder: 'Enter the JSON Allesseh query for Barrons queries',
      field: baseBarronsAllessehQuery
    },
    {
      key: 'baseBarronsVideo',
      label: 'Base Barrons Video Allesseh Query',
      placeholder: 'Enter the JSON Allesseh query for Barrons Video queries',
      field: baseBarronsVideoAllessehQuery
    },
    {
      key: 'baseBarronsInvestorCircle',
      label: 'Base Barrons Investor Circle Allesseh Query',
      placeholder: 'Enter the JSON Allesseh query for Barrons Investor Circle queries',
      field: baseBarronsInvestorCircleAllessehQuery
    },
    {
      key: 'baseBarronsLiveCoverageEvents',
      label: 'Base Barrons Live Coverage Events Allesseh Query',
      placeholder: 'Enter the JSON Allesseh query for Barrons Live Coverage Events queries',
      field: baseBarronsLiveCoverageEventsAllessehQuery
    },
    {
      key: 'baseBarronsLiveCoverageCards',
      label: 'Base Barrons Live Coverage Cards Allesseh Query',
      placeholder: 'Enter the JSON Allesseh query for Barrons Live Coverage Cards queries',
      field: baseBarronsLiveCoverageCardsAllessehQuery
    },
    {
      key: 'baseDowJonesRiskJournal',
      label: 'Base Dow Jones Risk Journal Allesseh Query',
      placeholder: 'Enter the JSON Allesseh query for Dow Jones Risk Journal queries',
      field: baseDowJonesRiskJournalAllessehQuery
    },
    {
      key: 'baseDowJonesRiskJournalDJEvents',
      label: 'Base Dow Jones Risk Journal DJ Events Allesseh Query',
      placeholder: 'Enter the JSON Allesseh query for Dow Jones Risk Journal DJ Events queries',
      field: baseDowJonesRiskJournalDJEventsAllessehQuery
    },
    {
      key: 'baseDowJonesRiskJournalCCOEvents',
      label: 'Base Dow Jones Risk Journal CCO Events Allesseh Query',
      placeholder: 'Enter the JSON Allesseh query for Dow Jones Risk Journal CCO Events queries',
      field: baseDowJonesRiskJournalCCOEventsAllessehQuery
    },
    {
      key: 'baseDowJonesRiskJournalContributor',
      label: 'Base Dow Jones Risk Journal Contributor Allesseh Query',
      placeholder: 'Enter the JSON Allesseh query for Dow Jones Risk Journal Contributor queries',
      field: baseDowJonesRiskJournalContributorAllessehQuery
    },
    {
      key: 'baseDragonfly',
      label: 'Base Dragonfly Allesseh Query',
      placeholder: 'Enter the JSON Allesseh query for Dragonfly queries',
      field: baseDragonflyAllessehQuery
    },
    {
      key: 'basePENEWS',
      label: 'Base PENEWS Allesseh Query',
      placeholder: 'Enter the JSON Allesseh query for PENEWS queries',
      field: basePENEWSAllessehQuery
    },
    {
      key: 'baseAfp',
      label: 'Base AFP Allesseh Query',
      placeholder: 'Enter the JSON Allesseh query for AFP queries',
      field: baseAfpAllessehQuery
    },
    {
      key: 'baseExportComplianceManager',
      label: 'Base Export Compliance Manager Allesseh Query',
      placeholder: 'Enter the JSON Allesseh query for Export Compliance Manager queries',
      field: baseExportComplianceManagerAllessehQuery
    },
    {
      key: 'baseFNOnline',
      label: 'Base FN Online Allesseh Query',
      placeholder: 'Enter the JSON Allesseh query for FN Online queries',
      field: baseFNOnlineAllessehQuery
    },
    {
      key: 'baseFreeExpression',
      label: 'Base Free Expression Allesseh Query',
      placeholder: 'Enter the JSON Allesseh query for Free Expression queries',
      field: baseFreeExpressionAllessehQuery
    },
    {
      key: 'baseOxfordAnalytica',
      label: 'Base Oxford Analytica Allesseh Query',
      placeholder: 'Enter the JSON Allesseh query for Oxford Analytica queries',
      field: baseOxfordAnalyticaAllessehQuery
    },
    {
      key: 'baseOxfordAnalyticaAnnouncements',
      label: 'Base Oxford Analytica Announcements Allesseh Query',
      placeholder: 'Enter the JSON Allesseh query for Oxford Analytica Announcements queries',
      field: baseOxfordAnalyticaAnnouncementsAllessehQuery
    },
    {
      key: 'basePenta',
      label: 'Base Penta Allesseh Query',
      placeholder: 'Enter the JSON Allesseh query for Penta queries',
      field: basePentaAllessehQuery
    },
    {
      key: 'baseWorldECR',
      label: 'Base World ECR Allesseh Query',
      placeholder: 'Enter the JSON Allesseh query for World ECR queries',
      field: baseWorldECRAllessehQuery
    },
    {
      key: 'baseBarronsBlog',
      label: 'Base Barrons Blog Allesseh Query',
      placeholder: 'Enter the JSON Allesseh query for Barrons Blog queries',
      field: baseBarronsBlogAllessehQuery
    },
    {
      key: 'baseIbd',
      label: 'Base IBD Allesseh Query',
      placeholder: 'Enter the JSON Allesseh query for IBD queries',
      field: baseIbdAllessehQuery
    },
    {
      key: 'baseWsjProPE',
      label: 'Base WSJ Pro PE Allesseh Query',
      placeholder: 'Enter the JSON Allesseh query for WSJ Pro PE queries',
      field: baseWsjProPEAllessehQuery
    },
    {
      key: 'baseOpis',
      label: 'Base OPIS Allesseh Query',
      placeholder: 'Enter the JSON Allesseh query for OPIS queries',
      field: baseOpisAllessehQuery
    },
    {
      key: 'baseEnergyNewsEvents',
      label: 'Base Energy News Events Allesseh Query',
      placeholder: 'Enter the JSON Allesseh query for Energy News Events queries',
      field: baseEnergyNewsEventsAllessehQuery
    },
  {
    key: 'basePrivateMarketEvents',
    label: 'Base Private Market Events Allesseh Query',
    placeholder: 'Enter the JSON Allesseh query for Private Market Events queries',
    field: basePrivateMarketEventsAllessehQuery
  }
  ];

  const { isReadOnly } = publishingFragment;

  return (
    <Group gap="lg" direction="column">
      {!currentPublicationSetting ? (
        <ResponsiveLoader />
      ) : (
        <>
          <Group.Item fullWidth>
            <PublicationSettingsActionBar
              onSave={handleSave}
              hasChanged={hasChanged}
              isSaving={isUpdating}
              isLoading={isLoading}
              publishingFragment={publishingFragment}
              lockIsReady={lockIsReady}
              isPageLockingEnabled={isPageLockingEnabled}
              lockLoading={lockLoading}
              onLock={handleLock}
              onRelease={handleRelease}
            />
          </Group.Item>

          <Group.Item fullWidth>
            <Box>
              <Box.Title>Content Sources</Box.Title>
              <Box.Content>
                <Typography variant="note">
                  Base Allesseh queries, per-publication query configs, and the auto-refresh cadence that drives the
                  content-search panel. Directly affects what shows up in curation and what is eligible for live polling.
                </Typography>
                <Group gap="mlg" direction="column" fullWidth margin={{ top: 'md' }}>
                  <Group gap="xs" direction="column" fullWidth>
                    <MultiSelect
                      label="Editable External Publications"
                      disabled={isLoading || isReadOnly}
                      items={properties?.map((property) => toMultiselectItem(property.value)) ?? []}
                      placeholder="Select editable external publications"
                      selectedItems={selectedEditableExternalPublications.map(toMultiselectItem)}
                      onSelectedItemsChange={handleEditableExternalPublicationsChange}
                      resetPosition="inside"
                    />
                    <FormHelperText>
                      Other publications whose content this brand&apos;s editors can edit directly from this UCS
                      instance.
                    </FormHelperText>
                  </Group>
                  <Group gap="xs" direction="column" fullWidth>
                    <Typography variant="header4">Base Allesseh Query &amp; Base Query Configs</Typography>
                    <FormHelperText>
                      The primary <em>Base Allesseh Query</em> plus the newer array-style <em>Base Query Configs</em>.
                      These are merged into every module/collection query for this brand — &quot;what article pool this
                      brand is allowed to see&quot; per content type.
                    </FormHelperText>
                    <Accordion title="Edit base queries">
                      <UnifiedQueryConfigTable
                        publicationKey={currentProperty ?? ''}
                        baseQueryConfigs={
                          (data?.publicationSetting.baseQueryConfigs ??
                            []) as import('data/generated/graphql').QueryConfig[]
                        }
                        isLoading={isLoading}
                        isReadOnly={isReadOnly}
                        staticRows={baseQueryFields.map(({ key, label, field }) => ({ key, label, jsonField: field }))}
                        onSaveStaticRow={async () => {
                          const newPublicationSetting = buildPublicationSettingInput();
                          setCurrentPublicationSetting(newPublicationSetting);
                          await mutateAsync({ publicationSettingUpdateInput: newPublicationSetting });
                          setHasChanged(false);
                          jsonFields.forEach((f) => f.clearError());
                        }}
                      />
                    </Accordion>
                  </Group>
                  <Group gap="xs" direction="column" fullWidth>
                    <FormLabel
                      label="Content Search auto refresh timeout (seconds)"
                      labelPosition="top"
                      fullWidth
                    />
                    <FormHelperText>
                      How often the content-search panel polls Allesseh for new or updated articles. Lower is more
                      responsive but heavier on the backend; leave blank to disable auto-refresh. The value only takes
                      effect when the feature flags <em>AutorefreshContentSearchEnabled</em> (search-panel polling) and{' '}
                      <em>AutorefreshDraftArticlesEnabled</em> (draft-article polling in the curation view) are on.
                    </FormHelperText>
                    <Input
                      placeholder="Enter the auto refresh timeout for content search in seconds"
                      value={currentPublicationSetting.autoRefreshTimeout ?? ''}
                      onChange={handleInputNumberChange('autoRefreshTimeout')}
                      disabled={isReadOnly}
                    />
                  </Group>
                </Group>
              </Box.Content>
            </Box>
          </Group.Item>
        </>
      )}
    </Group>
  );
};

export default PublicationSettingsContentSources;
